import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Focus in the browser's clean layout pass, before paint, without flushing pending DOM writes. */
export function focusAfterLayout(target: HTMLElement): () => void {
  const focus = () => { if (target.isConnected) target.focus({ preventScroll: true }) }
  if (typeof ResizeObserver === 'undefined') { focus(); return () => {} }
  const observer = new ResizeObserver(() => { observer.disconnect(); focus() })
  observer.observe(target)
  return () => observer.disconnect()
}

/**
 * Modal focus handling: moves focus in (to `[data-autofocus]`, else the first control, else
 * the container), keeps Tab inside, and closes on Escape. Focus restore is the opener's job.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, onClose: () => void, enabled = true) {
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const el = ref.current
    // A layer that is on its way out (enabled = false) lets go of the keyboard right away.
    if (!el || !enabled) return
    // Something inside may already have asked for focus (e.g. search via the `/` shortcut).
    const focus = () => {
      if (el.contains(document.activeElement)) return
      const first = el.querySelector<HTMLElement>('[data-autofocus]') ?? el.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? el).focus({ preventScroll: true })
    }
    let observer: ResizeObserver | undefined
    if (!el.contains(document.activeElement)) {
      // Focus before paint in the clean layout pass, rather than flushing a newly mounted sheet.
      if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(() => { observer?.disconnect(); focus() }); observer.observe(el) }
      else focus()
    }
    const onKey = (e: KeyboardEvent) => {
      // A nested layer (a context menu, a sheet over the places list) handles its own keys.
      const top = [...document.querySelectorAll<HTMLElement>('[aria-modal="true"]')].pop()
      if (top && top !== el && !el.contains(top)) return
      const target = e.target as HTMLElement | null
      if (target?.closest?.('[role="menu"]')) return
      if (e.key === 'Escape') {
        // Escape in a search field clears it (or closes its results) first.
        if (target instanceof HTMLInputElement && (target.value || target.getAttribute('aria-expanded') === 'true')) return
        e.stopPropagation()
        close.current()
        return
      }
      if (e.key !== 'Tab') return
      // Only what Tab can actually reach: roving-tabindex tabs (-1) and hidden or inert
      // controls don't count, or the wrap would miss the real last stop.
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (n) => n.tabIndex >= 0 && !n.hasAttribute('disabled') && !n.closest('[aria-hidden="true"], [hidden], [inert]'),
      )
      if (!items.length) {
        e.preventDefault()
        return
      }
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey && (active === firstEl || !el.contains(active))) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && (active === lastEl || !el.contains(active))) {
        e.preventDefault()
        firstEl.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => { observer?.disconnect(); document.removeEventListener('keydown', onKey, true) }
  }, [ref, enabled])
}
